/**
 * Les logos de l'entreprise imprimés sur les documents (bon d'intervention, puis devis, factures…).
 *
 * Règle de poids de Benjamin (2026-10-16, non négociable) : un bon pèse moins de 150 Ko, car il s'envoie par courriel
 * par lots de quinze. Un logo déposé tel quel (photo de 2 Mo) ruinerait ce budget : le bureau le REDIMENSIONNE donc dans
 * le navigateur avant l'envoi — réduit à la largeur utile (jamais agrandi), rogné de ses marges vides, recodé en JPEG sur
 * fond blanc — et c'est cette image légère que le serveur stocke. Le serveur contrôle de toute façon l'octet reçu.
 *
 * Ce module est la partie PURE (dimensions, zone utile, contrôle du fichier, nom). Le dessin sur la toile et le recodage
 * vivent dans `preparerLogo`, qui a besoin du navigateur.
 */

/** Largeur (px) du logo de l'entreprise : à 36 mm d'impression, environ 300 points par pouce. */
export const LARGEUR_LOGO_PRINCIPAL = 480;
/** Largeur (px) d'un logo de partenaire ou de certification : à 28 mm d'impression, environ 300 points par pouce. */
export const LARGEUR_LOGO_SECONDAIRE = 330;
/** Au plus ce nombre de logos de partenaires (le serveur refuse au-delà). */
export const PLAFOND_LOGOS_SECONDAIRES = 3;
/**
 * Un logo recodé pèse au plus ce nombre d'octets : au-delà, la qualité baisse jusqu'à y tenir. Les trois logos d'un bon pèsent
 * ensemble une quarantaine de Ko sur les 150 Ko du budget : à 300 points par pouce, un logo n'a pas besoin de plus.
 */
export const POIDS_MAX_LOGO_PRINCIPAL = 16_000;
export const POIDS_MAX_LOGO_SECONDAIRE = 12_000;

export function poidsMaxLogo(role: RoleLogo): number {
  return role === 'principal' ? POIDS_MAX_LOGO_PRINCIPAL : POIDS_MAX_LOGO_SECONDAIRE;
}
/** Plus grand fichier d'origine accepté (avant réduction). */
export const TAILLE_MAX_ORIGINE = 10 * 1024 * 1024;

export type RoleLogo = 'principal' | 'secondaire';

export function largeurCible(role: RoleLogo): number {
  return role === 'principal' ? LARGEUR_LOGO_PRINCIPAL : LARGEUR_LOGO_SECONDAIRE;
}

/** Les formats que le navigateur sait dessiner sur une toile (un SVG, lui, est refusé par le serveur). */
export const FORMATS_LOGO = 'image/png,image/jpeg,image/webp,image/gif';

export interface ZoneUtile {
  readonly x: number;
  readonly y: number;
  readonly largeur: number;
  readonly hauteur: number;
}

/**
 * La zone qui contient le dessin : les pixels visibles (pas transparents, pas blancs) et rien d'autre, à un pixel près de
 * marge. `null` si l'image est entièrement vide ou blanche. `pixels` est le tableau RGBA d'une toile (4 valeurs par pixel).
 */
export function zoneUtile(pixels: ArrayLike<number>, largeur: number, hauteur: number): ZoneUtile | null {
  let x0 = largeur;
  let y0 = hauteur;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < hauteur; y++) {
    for (let x = 0; x < largeur; x++) {
      const i = (y * largeur + x) * 4;
      const alpha = pixels[i + 3] ?? 0;
      const rouge = pixels[i] ?? 0;
      const vert = pixels[i + 1] ?? 0;
      const bleu = pixels[i + 2] ?? 0;
      const visible = alpha > 40 && !(rouge > 240 && vert > 240 && bleu > 240);
      if (!visible) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  if (x1 < 0) return null;
  x0 = Math.max(0, x0 - 1);
  y0 = Math.max(0, y0 - 1);
  x1 = Math.min(largeur - 1, x1 + 1);
  y1 = Math.min(hauteur - 1, y1 + 1);
  return { x: x0, y: y0, largeur: x1 - x0 + 1, hauteur: y1 - y0 + 1 };
}

/**
 * Les dimensions du logo réduit : à la largeur cible, hauteur au prorata. Un logo plus étroit que la cible garde sa
 * taille (on ne l'agrandit jamais : agrandir ne ferait qu'alourdir et flouter).
 */
export function dimensionsReduites(
  largeurSource: number,
  hauteurSource: number,
  largeurMax: number,
): { readonly largeur: number; readonly hauteur: number } {
  const largeur = Math.max(1, Math.min(largeurMax, Math.round(largeurSource)));
  const hauteur = Math.max(1, Math.round((largeur * hauteurSource) / largeurSource));
  return { largeur, hauteur };
}

/** Pourquoi un fichier ne peut pas servir de logo, ou `null`. Le serveur contrôle les octets ; ceci évite un aller-retour. */
export function refusDuFichierLogo(fichier: { readonly name: string; readonly type: string; readonly size: number }): string | null {
  if (fichier.size === 0) return `« ${fichier.name} » est vide.`;
  if (fichier.size > TAILLE_MAX_ORIGINE) return `« ${fichier.name} » dépasse 10 Mo : choisissez une image plus légère.`;
  if (!/^image\/(png|jpeg|webp|gif)$/i.test(fichier.type) && !/\.(png|jpe?g|webp|gif)$/i.test(fichier.name)) {
    return `« ${fichier.name} » n’est pas une image PNG, JPEG, WEBP ou GIF.`;
  }
  return null;
}

/** Le nom du fichier déposé : sans rapport avec le nom d'origine (qui peut porter n'importe quoi), toujours en JPEG. */
export function nomFichierLogo(role: RoleLogo, rang = 1): string {
  return role === 'principal' ? 'logo-entreprise.jpg' : `logo-partenaire-${rang}.jpg`;
}

/** « 12,3 Ko » : le poids d'un logo, pour que Benjamin voie ce qui s'ajoute à son bon. */
export function poidsLisible(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  return `${(octets / 1024).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Ko`;
}

// ------------------------------------------------------------------- dans le navigateur

/** Les qualités JPEG essayées, de la meilleure à la plus basse, puis les échelles de réduction. */
const QUALITES = [0.9, 0.8, 0.7, 0.6, 0.5];
const ECHELLES = [1, 0.85, 0.7, 0.55];

/**
 * Lit le fichier comme une image. Elle passe par une adresse `data:` et JAMAIS par l'adresse temporaire du navigateur
 * (celle que fabrique `URL.createObjectURL`) : la politique de sécurité du site (`img-src 'self' data:`, voir le
 * Caddyfile) refuse cette dernière, et un logo parfaitement valide « n'a pas pu être lu comme une image » (constaté sur la
 * préprod le 2026-10-17, absent en local où aucune politique ne s'applique).
 */
function chargerImage(fichier: File): Promise<HTMLImageElement> {
  return new Promise((ok, ko) => {
    const echec = () => ko(new Error(`« ${fichier.name} » n’a pas pu être lu comme une image.`));
    const lecteur = new FileReader();
    lecteur.onerror = echec;
    lecteur.onload = () => {
      const image = new Image();
      image.onload = () => ok(image);
      image.onerror = echec;
      image.src = String(lecteur.result);
    };
    lecteur.readAsDataURL(fichier);
  });
}

function versBlob(toile: HTMLCanvasElement, qualite: number): Promise<Blob> {
  return new Promise((ok, ko) => {
    toile.toBlob((b) => (b ? ok(b) : ko(new Error('Le logo n’a pas pu être recodé.'))), 'image/jpeg', qualite);
  });
}

/**
 * Prépare le logo à l'envoi : rogné de ses marges, réduit à la largeur utile, recodé en JPEG sur fond blanc, avec la plus haute
 * qualité qui tient dans `poidsMaxLogo(role)`. Rend le fichier à déposer.
 */
export async function preparerLogo(fichier: File, role: RoleLogo, rang = 1): Promise<File> {
  const image = await chargerImage(fichier);
  const source = document.createElement('canvas');
  source.width = image.naturalWidth;
  source.height = image.naturalHeight;
  const g = source.getContext('2d');
  if (!g) throw new Error('Ce navigateur ne sait pas redimensionner une image.');
  g.drawImage(image, 0, 0);
  // Rognage : une photo ou un PNG entouré de blanc gaspillerait la hauteur de l'en-tête.
  const zone =
    zoneUtile(g.getImageData(0, 0, source.width, source.height).data, source.width, source.height) ?? {
      x: 0,
      y: 0,
      largeur: source.width,
      hauteur: source.height,
    };
  const pleine = dimensionsReduites(zone.largeur, zone.hauteur, largeurCible(role));

  // La plus haute qualité qui tient dans le poids permis ; si même la plus basse ne tient pas (une photo plutôt qu'un logo),
  // on réduit aussi les dimensions : le poids d'un document prime sur la finesse d'un logo.
  let blob: Blob | null = null;
  for (const echelle of ECHELLES) {
    const largeur = Math.max(1, Math.round(pleine.largeur * echelle));
    const hauteur = Math.max(1, Math.round(pleine.hauteur * echelle));
    const cible = document.createElement('canvas');
    cible.width = largeur;
    cible.height = hauteur;
    const c = cible.getContext('2d');
    if (!c) throw new Error('Ce navigateur ne sait pas redimensionner une image.');
    c.fillStyle = '#fff';
    c.fillRect(0, 0, largeur, hauteur);
    c.imageSmoothingQuality = 'high';
    c.drawImage(source, zone.x, zone.y, zone.largeur, zone.hauteur, 0, 0, largeur, hauteur);
    for (const qualite of QUALITES) {
      blob = await versBlob(cible, qualite);
      if (blob.size <= poidsMaxLogo(role)) return new File([blob], nomFichierLogo(role, rang), { type: 'image/jpeg' });
    }
  }
  if (!blob) throw new Error('Le logo n’a pas pu être recodé.');
  return new File([blob], nomFichierLogo(role, rang), { type: 'image/jpeg' });
}
